import { 
  Brain, Eye, Camera, Sparkles, Target, Zap, Settings, Globe, Database, 
  Smartphone, Users, TrendingUp, Shield, BarChart3, CheckCircle, Lock, Clock
} from 'lucide-react';
import { ProductConfigs } from './types';

export const aiBeautyPlatformsConfigs: ProductConfigs = {
  'perfect-corp-youCam': {
    keyBenefits: [
      {
        icon: Camera,
        title: 'AR Virtual Try-On Technology',
        description: 'Advanced augmented reality platform for virtual makeup and beauty product try-on experiences with photorealistic results.',
        metrics: '95% try-on accuracy rate',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Brain,
        title: 'AI Beauty Intelligence',
        description: 'Machine learning algorithms providing personalized beauty recommendations based on facial analysis and preferences.',
        metrics: '92% recommendation satisfaction',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Facial Recognition & Analysis',
        description: 'Sophisticated facial analysis technology identifying features, skin tone, and optimal beauty matches.',
        metrics: '98% facial analysis precision',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Sparkles,
        title: 'Beauty Filter Suite',
        description: 'Professional beauty filters and effects with real-time application and customisation options.',
        metrics: '500+ professional beauty filters',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Social Beauty Platform',
        description: 'Integrated social platform for beauty sharing, tutorials, and community engagement with AR features.',
        metrics: '4.8/5 social engagement rating',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'Brand Integration Excellence',
        description: 'Seamless integration with beauty brands for product try-ons, campaigns, and e-commerce experiences.',
        metrics: '1000+ brand partnerships',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Real-Time AR Processing',
        description: 'Ultra-fast augmented reality processing with real-time beauty effects and seamless user experiences.',
        metrics: 'Sub-50ms AR processing',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Beauty SDK',
        description: 'Comprehensive software development kit enabling custom beauty applications and branded experiences.',
        metrics: 'Full SDK customisation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Multi-Platform Integration',
        description: 'Cross-platform compatibility with mobile apps, web browsers, and social media platforms.',
        metrics: 'Universal platform support',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Beauty Product Database',
        description: 'Extensive database of beauty products with accurate color matching and virtual representation.',
        metrics: 'Database of 50K+ products',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile-First Beauty Experience',
        description: 'Optimized mobile experience with camera integration, touch controls, and social sharing.',
        metrics: '4.9/5 mobile user experience',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Beauty Analytics Suite',
        description: 'Advanced analytics tracking user engagement, product performance, and beauty trend insights.',
        metrics: 'Real-time beauty trend analysis',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'voir-color-matching': {
    keyBenefits: [
      {
        icon: Sparkles,
        title: 'Precision Color Matching',
        description: 'Advanced color science technology providing exact shade matching for cosmetics and beauty products.',
        metrics: '99.2% color accuracy rate',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Skin Tone Analysis',
        description: 'Sophisticated skin tone analysis identifying undertones, overtones, and optimal color recommendations.',
        metrics: '96% skin tone accuracy',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Product Recommendation Engine',
        description: 'Intelligent product matching system recommending perfect color matches across brands and products.',
        metrics: '94% customer satisfaction',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Camera,
        title: 'Multi-Light Analysis',
        description: 'Advanced lighting analysis ensuring accurate color matching across different lighting conditions.',
        metrics: 'Accurate in 15+ lighting types',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Universal Color Database',
        description: 'Comprehensive database of color formulations from leading beauty brands with cross-referencing.',
        metrics: 'Database of 100K+ shades',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Color Matching',
        description: 'Professional-grade color matching using smartphone cameras with clinical accuracy.',
        metrics: '4.8/5 mobile accuracy rating',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Color Intelligence',
        description: 'Machine learning algorithms continuously improving color matching accuracy and recommendation quality.',
        metrics: 'Continuous learning improvement',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Color Calibration',
        description: 'Advanced calibration tools for different devices, lighting conditions, and brand-specific requirements.',
        metrics: 'Device-specific calibration',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Cross-Brand Color Mapping',
        description: 'Universal color translation between different beauty brands and product lines.',
        metrics: 'Support for 200+ brands',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Zap,
        title: 'Instant Color Analysis',
        description: 'Real-time color analysis with immediate results and product recommendations.',
        metrics: 'Results in under 2 seconds',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Professional Integration',
        description: 'Integration tools for beauty professionals with advanced color matching workflows.',
        metrics: 'Professional accuracy standards',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Color Trend Analytics',
        description: 'Advanced analytics tracking color preferences, trends, and seasonal variations.',
        metrics: 'Predictive color trend analysis',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'terra-skincare-ai': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI Custom Skincare Creation',
        description: 'Advanced AI platform creating personalized skincare formulations based on individual skin analysis and needs.',
        metrics: '96% formulation effectiveness',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Eye,
        title: 'Deep Skin Analysis',
        description: 'Comprehensive skin analysis technology examining multiple skin factors for optimal product recommendations.',
        metrics: '98% skin assessment accuracy',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Sparkles,
        title: 'Personalized Formulations',
        description: 'Custom skincare product creation with personalized ingredient selection and concentration optimization.',
        metrics: '90% customer satisfaction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Targeted Skin Solutions',
        description: 'Precision targeting of specific skin concerns with customized active ingredients and delivery systems.',
        metrics: '85% concern improvement rate',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Skin Progress Tracking',
        description: 'Advanced tracking system monitoring skin improvements and adjusting formulations for optimal results.',
        metrics: '92% tracked improvement success',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Dermatologist Collaboration',
        description: 'Integration with dermatologists for professional oversight and advanced skin concern management.',
        metrics: 'Network of 300+ dermatologists',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Ingredient Intelligence Database',
        description: 'Comprehensive database of skincare ingredients with efficacy data and interaction analysis.',
        metrics: 'Database of 5K+ ingredients',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Formulation Engine',
        description: 'Advanced formulation technology creating custom products in real-time based on skin analysis.',
        metrics: 'Custom products in 48 hours',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Protocol Development',
        description: 'Flexible skincare protocol creation with personalized routines and product application guidance.',
        metrics: 'Unlimited protocol customisation',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Skin Tracking',
        description: 'Comprehensive mobile platform for skin tracking, product management, and progress monitoring.',
        metrics: '4.9/5 mobile tracking accuracy',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Global Ingredient Sourcing',
        description: 'Worldwide sourcing of premium skincare ingredients with quality verification and ethical standards.',
        metrics: 'Sourcing from 40+ countries',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Camera,
        title: 'Visual Skin Assessment',
        description: 'Advanced imaging technology for precise skin assessment and formulation optimization.',
        metrics: 'Professional imaging accuracy',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};